import { Card, CardHeader, CardBody, Progress, Chip, Skeleton } from "@heroui/react";
import { Coins, TrendingDown, Calendar } from "lucide-react";
import { useCreditBalance } from "@src/presentation/hooks/credits";

export function CreditDetailsCard() {
    const { data: creditBalance, isLoading, error } = useCreditBalance();

    if (isLoading) {
        return (
            <Card className="border border-border rounded shadow-none">
                <CardHeader className="border-b border-divider pb-4">
                    <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                        <Coins className="h-4 w-4" />
                        Credit Balance
                    </h3>
                </CardHeader>
                <CardBody className="p-6">
                    <div className="space-y-4">
                        <Skeleton className="h-16 rounded" />
                        <Skeleton className="h-4 rounded" />
                        <Skeleton className="h-20 rounded" />
                    </div>
                </CardBody>
            </Card>
        );
    }

    if (error || !creditBalance) {
        return (
            <Card className="border border-border rounded shadow-none">
                <CardHeader className="border-b border-divider pb-4">
                    <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                        <Coins className="h-4 w-4" />
                        Credit Balance
                    </h3>
                </CardHeader>
                <CardBody className="p-6">
                    <p className="text-sm text-default-500">Unable to load credit information</p>
                </CardBody>
            </Card>
        );
    }

    const { balance, currentPeriodStart, currentPeriodEnd, isActive } = creditBalance;
    const { remainingCredits, totalCredits, usedCredits, usagePercentage } = balance;

    const getProgressColor = () => {
        if (usagePercentage >= 90) return "danger";
        if (usagePercentage >= 70) return "warning";
        return "success";
    };

    const getStatusColor = () => {
        if (usagePercentage >= 90) return "danger";
        if (usagePercentage >= 70) return "warning";
        return "success";
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric"
        });
    };

    return (
        <Card className="border border-border rounded shadow-none">
            <CardHeader className="border-b border-divider pb-4">
                <div className="flex items-center justify-between w-full">
                    <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                        <Coins className="h-4 w-4" />
                        Credit Balance
                    </h3>
                    <Chip
                        size="sm"
                        variant="flat"
                        color={isActive ? "success" : "danger"}
                    >
                        {isActive ? "Active" : "Inactive"}
                    </Chip>
                </div>
            </CardHeader>
            <CardBody className="p-6">
                <div className="space-y-6">
                    {/* Credit Overview */}
                    <div className="flex items-center justify-between p-4 rounded-lg border border-border bg-default-50/50">
                        <div>
                            <p className="text-xs text-default-500 font-medium">Remaining Credits</p>
                            <p className="text-3xl font-bold text-foreground mt-1">
                                {remainingCredits.toLocaleString()}
                            </p>
                            <p className="text-xs text-default-500 mt-1">
                                of {totalCredits.toLocaleString()} total
                            </p>
                        </div>
                        <Chip
                            size="lg"
                            variant="flat"
                            color={getStatusColor()}
                            className="h-16 px-4"
                        >
                            <div className="flex flex-col items-center">
                                <span className="text-2xl font-bold">{usagePercentage}%</span>
                                <span className="text-xs">Used</span>
                            </div>
                        </Chip>
                    </div>

                    {/* Usage Progress */}
                    <div className="space-y-2">
                        <div className="flex items-center justify-between text-xs">
                            <span className="text-default-500 font-medium">Credit Usage</span>
                            <span className="text-foreground font-semibold">
                                {usedCredits.toLocaleString()} used
                            </span>
                        </div>
                        <Progress
                            value={usagePercentage}
                            color={getProgressColor()}
                            className="h-2"
                            aria-label="Credit usage progress"
                        />
                    </div>

                    {/* Statistics Grid */}
                    <div className="grid grid-cols-2 gap-3">
                        <div className="flex items-start gap-3 p-3 rounded border border-border bg-default-50/50">
                            <TrendingDown className="h-4 w-4 text-default-500 mt-0.5" />
                            <div className="flex-1 min-w-0">
                                <p className="text-xs text-default-500 font-medium">Used Credits</p>
                                <p className="text-lg font-bold text-foreground">
                                    {usedCredits.toLocaleString()}
                                </p>
                            </div>
                        </div>

                        <div className="flex items-start gap-3 p-3 rounded border border-border bg-default-50/50">
                            <Coins className="h-4 w-4 text-default-500 mt-0.5" />
                            <div className="flex-1 min-w-0">
                                <p className="text-xs text-default-500 font-medium">Total Credits</p>
                                <p className="text-lg font-bold text-foreground">
                                    {totalCredits.toLocaleString()}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Billing Period */}
                    <div className="flex items-start gap-3 p-3 rounded border border-border bg-primary/5">
                        <Calendar className="h-4 w-4 text-primary mt-0.5" />
                        <div className="flex-1">
                            <p className="text-xs text-default-500 font-medium">Current Billing Period</p>
                            <p className="text-sm font-medium text-foreground mt-1">
                                {formatDate(currentPeriodStart)} - {formatDate(currentPeriodEnd)}
                            </p>
                        </div>
                    </div>

                    {/* Low Credit Warning */}
                    {usagePercentage >= 80 && (
                        <div className={`flex items-start gap-3 p-3 rounded border ${usagePercentage >= 90 ? 'border-danger bg-danger/5' : 'border-warning bg-warning/5'
                            }`}>
                            <TrendingDown className={`h-4 w-4 mt-0.5 ${usagePercentage >= 90 ? 'text-danger' : 'text-warning'
                                }`} />
                            <div className="flex-1">
                                <p className={`text-sm font-medium ${usagePercentage >= 90 ? 'text-danger' : 'text-warning'
                                    }`}>
                                    {usagePercentage >= 90 ? 'Critical: Low Credits' : 'Warning: Credits Running Low'}
                                </p>
                                <p className="text-xs text-default-500 mt-1">
                                    {usagePercentage >= 90
                                        ? 'You have used 90% or more of your credits. Please consider topping up soon.'
                                        : 'You have used 80% or more of your credits. Consider monitoring your usage.'}
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </CardBody>
        </Card>
    );
}
